// Wave 20.16 — Integrations page.
//
// Grouped into sections (Training, Health, Social). Each provider
// is a soon-chipped placeholder until the backend integrations
// service ships. Strava intentionally omitted — aero.chat competes
// with Strava, so we don't sync in either direction.

import React from 'react';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WatchIcon from '@mui/icons-material/Watch';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import { Box, Chip, Stack, Typography } from '@mui/material';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

interface Provider {
  id: string;
  name: string;
  note: string;
  Icon: React.ElementType;
  // Wave 20.49cp — live today (no connect step). READY chip
  // instead of SOON.
  ready?: boolean;
}

interface Section {
  id: string;
  title: string;
  description: string;
  providers: Provider[];
}

const SECTIONS: Section[] = [
  {
    id: 'training',
    title: 'Training',
    description: 'Push structured workouts + summaries to your training log.',
    providers: [
      { id: 'trainingpeaks', name: 'TrainingPeaks',  note: 'Push structured workouts + summaries',   Icon: ShowChartIcon },
      { id: 'garmin',        name: 'Garmin Connect', note: 'Import rides + runs recorded on device', Icon: WatchIcon },
    ],
  },
  {
    id: 'health',
    title: 'Health',
    description: 'Sync heart rate, VO₂ max, and workouts with your health record.',
    providers: [
      { id: 'apple-health',  name: 'Apple Health',   note: 'Read HR / VO₂ max, write workouts',      Icon: FavoriteBorderIcon },
    ],
  },
  {
    id: 'social',
    title: 'Social',
    description: 'Share session cards straight from any ride — no account linking needed.',
    providers: [
      { id: 'instagram', name: 'Instagram', note: 'Open a session → Share card → post to Stories', Icon: CameraAltIcon, ready: true },
      { id: 'facebook',  name: 'Facebook',  note: 'Open a session → Share card → pick Facebook in the share sheet', Icon: GroupsIcon, ready: true },
    ],
  },
];

export default function Integrations() {
  return (
    <PageLayout maxWidth="md">
      <Stack spacing={4}>
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Integrations
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Connect the tools you already use
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 0.75 }}>
            Two-way sync with the platforms users already use. We'll email when it's ready.
          </Typography>
        </Box>

        {SECTIONS.map((section) => (
          <Box key={section.id}>
            <Typography
              variant="overline"
              sx={{
                color: colors.brand,
                letterSpacing: '0.18em',
                fontWeight: 700,
              }}
            >
              {section.title}
            </Typography>
            <Typography sx={{ color: colors.textMuted, mt: 0.25, mb: 1.5, fontSize: '0.9rem' }}>
              {section.description}
            </Typography>
            <Stack spacing={1.5}>
              {section.providers.map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <p.Icon sx={{ color: colors.textMuted, fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: colors.text }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      {p.note}
                    </Typography>
                  </Box>
                  <Chip
                    label={p.ready ? 'READY' : 'SOON'}
                    size="small"
                    sx={{
                      backgroundColor: `${colors.brand}22`,
                      color: colors.brand,
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      height: 22,
                      fontSize: '0.65rem',
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        ))}

        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            backgroundColor: colors.surface,
            border: `1px dashed ${colors.border}`,
          }}
        >
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', lineHeight: 1.6 }}>
            Missing a platform? Tell us what you'd like to see —{' '}
            <Box component="a" href="mailto:hello@aero.chat" sx={{ color: colors.brand, textDecoration: 'none' }}>
              hello@aero.chat
            </Box>
            .
          </Typography>
        </Box>
      </Stack>
    </PageLayout>
  );
}
